import React, { useMemo } from 'react';
import { KeyRound, Shield } from 'lucide-react';
import DrawerShell from '@/components/DrawerShell';
import { Button } from '@/components/ui/button';
import {
  systemRoleLabel, systemRoleHint, moduleHint, coveragePct,
} from '../utils/rbacLabels';
import { IITH, SERIF } from '@/pages/landing/iithBrand';

/** Drill-down drawer listing permissions granted to a role. */
export default function RolePermissionsDrawer({
  open, onClose, role, permissions = [], permissionTotal = 0, onEditMapping,
}) {
  const codes = role?.permission_codes || [];
  const byCode = useMemo(() => {
    const m = new Map();
    permissions.forEach((p) => m.set(p.code, p));
    return m;
  }, [permissions]);

  const grouped = useMemo(() => {
    const map = new Map();
    codes.forEach((code) => {
      const p = byCode.get(code) || { code, name: code, module: 'Other' };
      const mod = p.module || 'Other';
      if (!map.has(mod)) map.set(mod, []);
      map.get(mod).push(p);
    });
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [codes, byCode]);

  const total = permissionTotal || permissions.length || 1;
  const pct = coveragePct(codes.length, total);

  if (!open || !role) return null;

  return (
    <DrawerShell
      open={open}
      onClose={onClose}
      eyebrow="Role permissions"
      title={role.name}
      subtitle={`${systemRoleLabel(role.system_role)} · ${codes.length} capabilities granted`}
      accent={IITH.navy}
      icon={<Shield className="h-5 w-5" />}
      width="lg"
      testId="role-permissions-drawer"
      headerExtras={(
        <div className="grid grid-cols-3 gap-2 pt-1">
          {[
            { l: 'Granted', v: codes.length },
            { l: 'Coverage', v: `${pct}%` },
            { l: 'Modules', v: grouped.length },
          ].map((s) => (
            <div key={s.l} className="rounded-xl bg-white/10 ring-1 ring-white/20 px-3 py-2.5 backdrop-blur-sm">
              <p className="text-[10px] uppercase tracking-wider text-white/65">{s.l}</p>
              <p className="text-lg font-semibold text-white tabular-nums" style={{ fontFamily: SERIF }}>
                {s.v}
              </p>
            </div>
          ))}
        </div>
      )}
      footer={(
        <>
          <Button variant="outline" onClick={onClose}>Close</Button>
          {onEditMapping ? (
            <Button
              onClick={() => onEditMapping(role)}
              style={{ background: IITH.saffron }}
              className="text-white"
              data-testid="role-perms-edit"
            >
              <KeyRound className="h-4 w-4 mr-1.5" /> Edit mapping
            </Button>
          ) : null}
        </>
      )}
    >
      <div className="px-6 py-5 space-y-4 overflow-y-auto">
        <p className="text-sm text-zinc-600 leading-relaxed">
          {role.description || systemRoleHint(role.system_role) || 'Permissions unlock screens and actions for every user on this role.'}
        </p>
        <div className="h-2 rounded-full bg-zinc-100 overflow-hidden">
          <div
            className="h-full rounded-full"
            style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${IITH.navy}, ${IITH.saffron})` }}
          />
        </div>

        {grouped.length === 0 ? (
          <p className="text-sm text-zinc-500 rounded-xl border border-dashed px-4 py-8 text-center">
            No permissions granted yet. Use Permissions to map this role.
          </p>
        ) : (
          grouped.map(([module, items]) => (
            <section key={module} className="rounded-xl border overflow-hidden">
              <header className="px-4 py-2.5 bg-[#E8EEF5]/80 border-b flex items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-zinc-900">{module}</p>
                  <p className="text-[11px] text-zinc-500">{moduleHint(module)}</p>
                </div>
                <span className="text-[11px] font-semibold tabular-nums text-zinc-500">
                  {items.length}
                </span>
              </header>
              <ul className="divide-y">
                {items.map((p) => (
                  <li key={p.code} className="px-4 py-2.5 flex items-start gap-3">
                    <span
                      className="mt-1.5 h-2 w-2 rounded-full shrink-0"
                      style={{ background: IITH.saffron }}
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-zinc-900">{p.name}</p>
                      <p className="text-[11px] font-mono text-zinc-500">{p.code}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))
        )}
      </div>
    </DrawerShell>
  );
}
